import useFunctionCall from '@/service/useFunctionCall';
import AccountCheckPopup from './AccountCheckPopup.vue';
import { monitor } from '@/common';

/**
 * 按钮配置类型定义
 */
type ButtonConfig = {
  text: string;
  onClick: () => void;
};

/**
 * 单按钮配置 (仅确认按钮)
 */
type SingleButtonConfig = [ButtonConfig];

/**
 * 双按钮配置 (确认 + 取消)
 */
type DoubleButtonConfig = [
  {
    text: string; // 确认按钮
    onClick: () => void;
  },
  {
    text: string; // 取消按钮
    onClick: () => void;
  }
];

/**
 * 浮层显示参数接口
 */
interface ShowParams {
  /** 浮层标题, 支持 \n 换行 */
  title: string;
  /** 浮层内容描述 */
  content: string;
  /** 自定义样式类名 */
  className?: string;
  /** 浮层图片 */
  imageUrl?: string;
  /** 按钮配置, 支持单按钮或双按钮模式 */
  button: SingleButtonConfig | DoubleButtonConfig;
}

/**
 * 账户检查浮层 Service 接口
 */
interface IAccountCheckPopup {
  /**
   * 显示浮层
   * @param params - 浮层配置参数
   * @example
   * // 双按钮模式 (确认 + 取消)
   * accountCheckPopup.show({
   *   title: '更新身份信息\n即可进行余额提现',
   *   content: '您的身份证件已过期,请尽快更新证件。',
   *   className: 'custom-popup',
   *   button: [
   *     {
   *       text: '立即更新',
   *       onClick: () => {
   *         console.log('用户点击确认');
   *       }
   *     },
   *     {
   *       text: '暂不更新',
   *       onClick: () => {
   *         console.log('用户点击取消');
   *       }
   *     }
   *   ]
   * });
   *
   * @example
   * // 单按钮模式 (仅确认)
   * accountCheckPopup.show({
   *   title: '提示',
   *   content: '操作成功',
   *   button: [
   *     {
   *       text: '确定',
   *       onClick: () => {
   *         console.log('用户点击确定');
   *       }
   *     }
   *   ]
   * });
   */
  show: (params: ShowParams) => void;

  /**
   * 隐藏浮层
   * @example
   * accountCheckPopup.hide();
   */
  hide: () => void;
}

/**
 * 创建账户检查浮层 Service 实例
 * 使用 useFunctionCall 实现函数式调用, 支持动态创建和销毁浮层组件
 */
const accountCheckPopupService = (() => {
  const { show: showComponent, close: closeComponent } = useFunctionCall<any>(AccountCheckPopup);

  const service: IAccountCheckPopup = {
    /**
     * 显示浮层
     */
    show(params: ShowParams): void {
      const { title, content, className = '', imageUrl, button } = params;

      // 判断是单按钮还是双按钮模式
      const isSingleButton = button.length === 1;

      // 构建 props 数据
      const propsData = {
        show: true,
        title,
        content,
        className,
        ...(imageUrl && { imageUrl }),
        // 根据按钮数量设置按钮文本
        ...(isSingleButton
          ? {
              confirmButton: button[0].text,
              cancelButton: '', // 单按钮模式下隐藏取消按钮
            }
          : {
              confirmButton: button[0].text,
              cancelButton: button[1].text,
            }),
      };

      // 构建事件处理器
      const eventHandlers: any = {
        // 确认按钮点击事件
        confirm: () => {
          if (button[0] && typeof button[0].onClick === 'function') {
            button[0].onClick();
          } else {
            monitor.captureMessage('余额账户管控浮层-确认按钮 onClick 为空!');
          }
        },
        // 取消按钮点击事件 (仅双按钮模式)
        cancel: () => {
          if (!isSingleButton && button[1] && typeof button[1].onClick === 'function') {
            button[1].onClick();
          } else {
            monitor.captureMessage('余额账户管控浮层-取消按钮 onClick 为空!');
          }
        },
        // 关闭浮层事件
        closeSlider: () => {
          closeComponent();
        },
      };

      // 调用 useFunctionCall 的 show 方法
      // 参数说明:
      // 1. propsData: 传递给组件的 props
      // 2. eventHandlers: 事件处理器对象
      // 3. hideTriggerEventName: 触发关闭的事件名 ('closeSlider')
      showComponent(propsData, eventHandlers, 'closeSlider');
    },

    /**
     * 隐藏浮层
     */
    hide(): void {
      closeComponent();
    },
  };

  return service;
})();

export default accountCheckPopupService;
